import React from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import { RowExpansion } from "@design-system/ViewTable/RowExpansion";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { BalanceSheetDerivativeLines } from "@common/constants/domains/accounting/accounts";
import { isGroupEndPeriod, isNLevelClassReport } from "@common/helpers/reports";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { GlTableCallbacks } from "../glDrillDown.lib";
import { BalanceSheetViewValueCell } from "./BalanceSheetViewValueCell";
import { BalanceSheetViewRow } from "./balanceSheetViewTypes";
import styles from "./BalanceSheetTable.module.scss";

export const BALANCING_ROW_TOOLTIP =
    "For entries with a partial class assignment. Amounts not tagged to this class are included in No class or another class.";

export function isBalancingReportRow(row: {
    label: string;
    accountCodes: string[];
}): boolean {
    return (
        row.label === BalanceSheetDerivativeLines.unclassified &&
        row.accountCodes.length === 0
    );
}

interface MakePeriodCellOptions {
    period: ReportPeriodWithLabel;
    periodIndex: number;
    allPeriods: ReportPeriodWithLabel[];
}

export const makeBalanceSheetPeriodCell = ({
    period,
    periodIndex,
    allPeriods,
}: MakePeriodCellOptions) => {
    const isTotalPeriod = Boolean(period.isTotal);
    // N-level draws full-height separators at the table level, making the legacy
    // per-cell group-end divider redundant.
    const isNLevelClassMode = isNLevelClassReport(allPeriods);
    const isGroupEnd =
        !isTotalPeriod &&
        !isNLevelClassMode &&
        isGroupEndPeriod(allPeriods, periodIndex);

    const BalanceSheetPeriodCell = ({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        BalanceSheetViewRow,
        unknown
    >): React.ReactElement => {
        const { values, accountCodes, isTotal } = row.original;
        const onDrillDown = useViewTableMeta<GlTableCallbacks, unknown>(table)
            .callbacks?.onDrillDown;
        const value = values[periodIndex] ?? 0;

        return (
            <RowExpansion row={row}>
                {(isExpanded) => {
                    const shouldShowValues = !row.getCanExpand() || !isExpanded;
                    // Section headers show empty cells while expanded.
                    return shouldShowValues ? (
                        <BalanceSheetViewValueCell
                            value={value}
                            accountCodes={
                                isTotalPeriod && period.comparisonDiff
                                    ? []
                                    : accountCodes
                            }
                            period={period}
                            isTotal={isTotal}
                            isGroupEnd={isGroupEnd}
                            tooltip={
                                isBalancingReportRow(row.original)
                                    ? BALANCING_ROW_TOOLTIP
                                    : undefined
                            }
                            onDrillDown={onDrillDown}
                        />
                    ) : (
                        <ViewTableCell
                            className={classNames(styles.numericCell, {
                                [styles.totalCell]: isTotal,
                            })}
                        />
                    );
                }}
            </RowExpansion>
        );
    };

    return BalanceSheetPeriodCell;
};
